import React, { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useTheme } from '@mui/material/styles'
import { useParams } from 'react-router-dom'
import { useAppDispatch } from '../../../../store'
import { getEnrollmentsApi } from '../../../../store/slices/termsSectionsSlice/api'
import { createUserRolesApi, updateUserRolesApi } from '../../../../store/slices/roleSlice/api'
import { getActionLoading } from '../../../../store/slices/roleSlice/selectors'
import { getTerm } from '../../../../store/slices/termsSlice/selectors'
import ValidationAlert from '../../../../components/ValidationAlert'
import { bindSetError, showServerFieldErrors } from '../../../../utils/showServerFieldErrors'
import DefaultModalActions from '../../../../components/GenericModal/DefaultModalActions'
import MultiSelectApiAutoComplete from '../../../../components/FormElements/MultiSelectApiAutoComplete'
import { ApiAutoComplete } from '../../../../components/FormElements/ApiAutoComplete'
import MultiSelectWithCheckBox from '../../../../components/FormElements/MultiSelectWithCheckBox'
import { type IEnrollment } from '../../../../models/Enrollment'
import { LastUpdateMeta } from '../../../../components/GenericModal/ChangeMetaInfo'
import { useAccess } from '../../../../hooks/useAccess'
import { READONLY_TOOLTIP } from '../../../../consts'

interface IUser {
  userId: string
  name: string
  email: string
  isInstructorSelected?: boolean
}
interface EnrollmentCellData {
  userId?: string
  email?: string
  section?: string
  sectionId?: string
  instructors?: IUser[]
}

interface FormType {
  email: string
  sectionId: string
  instructors: IUser[]
}

interface Props {
  handleCloseModal?: () => void
  params?: Record<string, any>
  isEditAction?: boolean
  selectedRow?: IEnrollment
  allowedActions?: string[]
  isTaskRunning?: boolean
  onTaskBlock?: () => void
}

export const CreateEnrollment: React.FC<Props> = ({
  handleCloseModal = () => { },
  params,
  isEditAction = false,
  allowedActions,
  selectedRow,
  isTaskRunning,
  onTaskBlock
}) => {
  useEffect(() => {
    if (selectedRow != null && isTaskRunning) {
      onTaskBlock?.()
      handleCloseModal()
    }
  }, [selectedRow])

  const theme = useTheme()
  const dispatch = useAppDispatch()
  const { id } = useParams()
  const term = getTerm()
  const loading = getActionLoading()
  const [saving, setSaving] = React.useState(false)
  const [errors, setErrors] = useState<unknown>({})
  const { readonly, disabled, canCreate, canDelete, canEdit } = useAccess(allowedActions ?? [])

  const schema = yup.object({
    email: yup.string().required('Student email is required'),
    sectionId: yup.string().required('Section is required'),
    instructors: yup
      .array()
      .of(
        yup.object({
          userId: yup.string().required(),
          name: yup.string().required(),
          email: yup.string().required()
        })
      )
      .default([])
  })

  const methods = useForm<FormType>({
    resolver: yupResolver(schema),
    defaultValues: {
      email: selectedRow?.user?.email ?? '',
      sectionId: selectedRow?.section?.sectionId ?? '',
      instructors: selectedRow?.instructors?.filter(instructor => instructor?.isInstructorSelected) ?? []
    }
  })

  const { handleSubmit, watch, setValue } = methods
  const sectionId = watch('sectionId')
  const email = watch('email')

  React.useEffect(() => {
    if (!sectionId) {
      setValue('instructors', [])
    }
  }, [sectionId, setValue])

  const onFormSubmit = (e?: React.BaseSyntheticEvent): void => {
    void handleSubmit(onSubmit)(e)
  }

  async function onSubmit (data: FormType): Promise<void> {
    setSaving(true)
    setErrors({})
    try {
      const instructorIds = data.instructors.map(i => i.userId)
      let payload
      if (isEditAction) {
        payload = {
          instructors: instructorIds,
          sectionId: selectedRow?.section?.sectionId ?? '',
          userId: selectedRow?.user?.userId ?? ''
        }
      } else {
        payload = {
          objectId: data?.sectionId,
          email: data?.email,
          roleId: 'Student',
          termId: term?.data?.termId,
          instructors: instructorIds
        }
      }
      if (isEditAction) {
        await dispatch(updateUserRolesApi({ urlId: selectedRow?.user?.userId ?? '', data: payload })).unwrap()
      } else {
        await dispatch(createUserRolesApi({ data: payload })).unwrap()
      }
      await dispatch(getEnrollmentsApi({ urlId: id, params })).unwrap()
      handleCloseModal()
    } catch (error) {
      setErrors(showServerFieldErrors(error, bindSetError(methods.setError), methods.clearErrors))
    } finally {
      setSaving(false)
    }
  }

  return (
    <FormProvider {...methods}>
      <ValidationAlert errors={errors} />

      <form onSubmit={onFormSubmit}>
        <Box display="flex" flexDirection="column" gap={theme.spacing(6)}>
          <ApiAutoComplete
            apiEndpoint="/api/users/summary/"
            label="Student"
            labelField={!isEditAction ? 'name' : 'email'}
            name="email"
            valueField="email"
            readOnly={isEditAction}
            skip={isEditAction}
            selectedItem={selectedRow?.user}
            required={!isEditAction}
            getOptionSubLabel={(option: any) => option?.email}
            onChange={(newValue: any) => {
              setValue('email', newValue?.email ?? '', { shouldValidate: true })
            }}
          />

          <ApiAutoComplete
            apiEndpoint={`/api/sections/summary/?term_id=${id}`}
            label="Course Section"
            labelField="abbreviation"
            name="sectionId"
            getOptionSubLabel={(option: any) => option?.title}
            readOnly={isEditAction}
            skip={isEditAction}
            selectedItem={selectedRow?.section}
            required={!isEditAction}
            onChange={(newValue: any) => {
              setValue('sectionId', newValue?.sectionId ?? '', { shouldValidate: true })
            }}
          />
          {
            !isEditAction
              ? <MultiSelectApiAutoComplete
                apiEndpoint={`/api/sections/${sectionId}/instructors/`}
                skip={!sectionId || !email}
                sortField="name"
                label="Instructors"
                name="instructors"
                placeholder="Instructors"
                getOptionLabel={(option: IUser) => option?.name}
                getOptionSubLabel={(option: IUser) => option?.email}
                getOptionValue={(option: IUser) => option?.userId}
                disabled={!sectionId || !email}
                showSelectAll
                renderTagsType='chips'
              />
              : <MultiSelectWithCheckBox
                label="Instructors"
                name="instructors"
                options={selectedRow?.instructors ?? []}
                placeholder={selectedRow?.instructors?.length ? 'Select instructor(s)' : 'No instructors available'}
                getOptionLabel={(option: IUser) => option?.name}
                getOptionSubLabel={(option: IUser) => option?.email}
                getOptionValue={(option: IUser) => option?.userId}
                fullWidth
                showSelectAll
                renderTagsType='chips'
                readonly={readonly}
              />
          }

        </Box>

        <DefaultModalActions
          destructiveButtonProps={{ onClick: handleCloseModal }}
          confirmButtonProps={{
            text: isEditAction ? 'Save' : 'Create',
            loading: loading || saving,
            type: 'submit',
            disabled: readonly,
            tooltip: readonly ? READONLY_TOOLTIP : undefined
          }}
          lastUpdateMeta={isEditAction && (
            <LastUpdateMeta lastEditedBy={selectedRow?.section?.modifiedByUsername} lastEditedAt={selectedRow?.section?.modifiedOn} />
          )}

        />
      </form>
    </FormProvider>
  )
}

export default CreateEnrollment
